import { MealMode, UnitMode } from '@/enums/nutrition-standard.enum';
import { AGE_TYPE_CONST } from '@/constants/type';

export const UNDER_24_MONTHS_AGE_IDS = [
  'DT00',
  'DT01',
  'DT02',
  'DT03',
  'DT04'
] as const;
export const UNDER_24_MONTHS_AGE_IDS_EXCLUDE_DT00 = [
  'DT01',
  'DT02',
  'DT03',
  'DT04'
] as const;
export const FROM_24_TO_60_MONTHS_AGE_IDS = ['DT05', 'DT06'] as const;
export const FROM_60_TO_72_MONTHS_AGE_IDS = ['DT07'] as const;
export const OVER_60_MONTHS_AGE_IDS = [
  'DT07',
  'DT08',
  'DT09',
  'DT10',
  'DT11',
  'DT12',
  'DT13',
  'DT14',
  'DT15'
] as const;
export const AGE_IDS_HAS_AFTERNOON_MEAL = [
  'DT03',
  'DT04',
  'DT05',
  'DT06',
  'DT07'
] as const;

export const AGE_GROUPS = {
  UNDER_60_MONTHS: [
    'DT00',
    'DT01',
    'DT02',
    'DT03',
    'DT04',
    'DT05',
    'DT06'
  ] as const,
  FROM_60_MONTHS_TO_19_YEARS: [
    'DT07',
    'DT08',
    'DT09',
    'DT10',
    'DT11',
    'DT12'
  ] as const,
  OVER_19_YEARS: ['DT13', 'DT14', 'DT15'] as const
};

export const MEALS_PER_DAY_OPTIONS = [
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 4, label: '4' },
  { value: 5, label: '5' }
];

export const ratioSignOptions = [
  { value: '>=', label: '≥' },
  { value: '<=', label: '≤' },
  { value: '>', label: '>' },
  { value: '<', label: '<' },
  { value: '=', label: '=' }
];

export const minMaxNutrition = [
  { key: 'min', name: 'Min' },
  { key: 'max', name: 'Max' }
];

export const mealsPerDay = [
  { key: MealMode.BREAKFAST, name: 'Sáng', data: minMaxNutrition },
  { key: MealMode.LUNCH, name: 'Trưa', data: minMaxNutrition },
  { key: MealMode.AFTERNOON_MEAL, name: 'Chiều', data: minMaxNutrition },
  { key: MealMode.EXTRA_MEAL_1, name: 'Phụ 1', data: minMaxNutrition },
  { key: MealMode.EXTRA_MEAL_2, name: 'Phụ 2', data: minMaxNutrition }
];

export const mealsDay = [
  { key: MealMode.EXTRA_MEAL_2, name: 'Cấu trúc bữa' },
  { key: MealMode.ALL, name: 'Cả ngày' },
  { key: MealMode.BREAKFAST, name: 'Bữa sáng (g)' },
  { key: MealMode.LUNCH, name: 'Bữa trưa (g)' },
  { key: MealMode.AFTERNOON_MEAL, name: 'Bữa chiều (g)' },
  { key: MealMode.DINNER, name: 'Bữa tối (g)' },
  { key: MealMode.EXTRA_MEAL, name: 'Phụ (g)' }
];

export const CountUnit = [
  { key: 'percent', name: '%', data: minMaxNutrition },
  { key: 'gram', name: 'g', data: minMaxNutrition }
];

export const unitNutrition = [
  { key: UnitMode.P, name: 'P', data: CountUnit },
  { key: UnitMode.L, name: 'L', data: CountUnit },
  { key: UnitMode.G, name: 'G', data: CountUnit }
];

export const COMMON_CLASS = 'grayStyle';

export const createColumn = (
  title: string,
  key: string,
  className?: string
): any => ({
  title,
  dataIndex: key,
  key,
  align: 'center' as const,
  className: className ? className : COMMON_CLASS
});

const normalizeComparatorPrefix = (value: string) =>
  value.replace(/^≥/, '>=').replace(/^≤/, '<=');

export function mapApiToFilteredNutritionStandard(apiItem: any): any {
  const nutritionalEnergyPlg = apiItem.nutritionalEnergyPlg || {};
  const nutritionalEnergyNeedPlg = apiItem.nutritionalEnergyNeed || {};
  const nutritionalIngredientDistribution =
    apiItem.nutritionalIngredientDistribution || {};
  const nutritionalMicro = apiItem.nutritionalMicroElements || {};
  const meals = apiItem.meals || [];
  // Backup fields
  const backupFields = Array.from({ length: 10 }, (_, i) => {
    const index = i + 1;
    return {
      [`backup${index}StandardDayMin`]:
        nutritionalMicro[`backup${index}StandardDayMin`] ?? '',
      [`backup${index}StandardDayMax`]:
        nutritionalMicro[`backup${index}StandardDayMax`] ?? ''
    };
  });

  const mealTypeKeyMap: Record<number, string> = {
    1: 'Breakfast',
    2: 'Lunch',
    3: 'Afternoon',
    4: 'Dinner',
    5: 'Extra1'
  };

  const nutrientKeys = ['P', 'L', 'G'];
  const unitKeys = ['percent', 'gram']; // gram viết tắt là 'g', percent viết đầy đủ
  const minMaxKeys = ['Min', 'Max'];

  const flatMealMap: Record<string, any> = {};

  meals.forEach((meal: any) => {
    const mealKey = mealTypeKeyMap[meal.mealTypeId];
    if (!mealKey) return;

    nutrientKeys.forEach((nutrient) => {
      unitKeys.forEach((unit) => {
        minMaxKeys.forEach((minMax) => {
          const unitKey = unit; // gram or percent

          const key = `standardPlg${mealKey}${nutrient}${unitKey}${minMax}`;

          const apiUnit = unit === 'gram' ? 'g' : 'Percent';
          const dataField = `plg${nutrient}${apiUnit}${minMax}`;

          flatMealMap[key] = meal[dataField] ?? '';
        });
      });
    });
  });

  return {
    id: apiItem.id,
    ageRangeId: apiItem.ageRangeId,
    ageRangeDescription: apiItem.ageRangeDescription,
    healthStatusId: apiItem.healthStatusId,
    healthStatusIds: apiItem.healthStatusIds ?? [],
    healthStatusName: apiItem.healthStatusName ?? '',
    genderId: apiItem.genderId,
    physicalActivityId: apiItem.physicalActivityId,
    weightMin: apiItem.weightMin,
    weightMax: apiItem.weightMax,
    nutritionStatusZscore: apiItem.nutritionStatusZscore,
    nutritionStatusBmiChild: apiItem.nutritionStatusBmiChild,
    nutritionStatusBmiAdult: apiItem.nutritionStatusBmiAdult,
    nutritionStatusZscoreName: apiItem.nutritionStatusZscoreName,
    nutritionStatusChildName: apiItem.nutritionStatusChildName,
    nutritionStatusAdultName: apiItem.nutritionStatusAdultName,
    physicalActivityLevelName: apiItem.physicalActivityLevelName,
    laborFactor: apiItem.laborFactor,
    createdAt: apiItem.createdAt,
    updatedAt: apiItem.updatedAt,
    breastMilkLevel: nutritionalEnergyPlg?.breastMilkLevel?.id ?? null,
    breastMilkLevelValue: nutritionalEnergyPlg?.breastMilkLevel?.kcal ?? null,
    lProportionOperator: nutritionalEnergyPlg?.lProportionOperator ?? '>=',
    lProportionValue: nutritionalEnergyPlg?.lProportionValue ?? '',
    pProportionOperator: (() => {
      const pProp = normalizeComparatorPrefix(
        nutritionalEnergyPlg?.pProportion ?? ''
      );
      if (!pProp) return '';
      const match = pProp.match(/^(>=|<=|>|<|=)/);
      const operator = match ? match[1] : pProp ? '=' : '';
      return operator;
    })(),
    pProportionValue: (() => {
      const pProp = normalizeComparatorPrefix(
        nutritionalEnergyPlg?.pProportion ?? ''
      );
      if (!pProp) return '';
      const match = pProp.match(/^(>=|<=|>|<|=)(.+)/);
      const value = match ? match[2] : pProp;
      return value;
    })(),

    metabolismBasic: nutritionalEnergyPlg?.metabolismBasic ?? '',
    energyConsumption: nutritionalEnergyPlg?.energyConsumption ?? '',
    energyNeeded: nutritionalEnergyPlg?.energyNeeded ?? '',
    energyMax: nutritionalEnergyPlg.energyMax,
    energyMin: nutritionalEnergyPlg.energyMin,
    complementaryFoodEnergyMin: nutritionalEnergyPlg.complementaryFoodEnergyMin,
    complementaryFoodEnergyMax: nutritionalEnergyPlg.complementaryFoodEnergyMax,
    breastMilkEnergy: nutritionalEnergyPlg.breastMilkEnergy ?? '',
    milkUnitsPerDay: nutritionalEnergyPlg.milkUnitsPerDay ?? '',

    // Protein
    standardPlgDayPgramMin: nutritionalEnergyPlg.plgDayPgMin ?? '',
    standardPlgDayPgramMax: nutritionalEnergyPlg.plgDayPgMax ?? '',
    standardPlgDayPpercentMin: nutritionalEnergyPlg.plgDayPPercentMin ?? '',
    standardPlgDayPpercentMax: nutritionalEnergyPlg.plgDayPPercentMax ?? '',
    standardPlgDayPRatio: normalizeComparatorPrefix(
      nutritionalEnergyPlg.pProportion ?? ''
    ),

    // Lipid
    standardPlgDayLgramMin: nutritionalEnergyPlg.plgDayLgMin ?? '',
    standardPlgDayLgramMax: nutritionalEnergyPlg.plgDayLgMax ?? '',
    standardPlgDayLpercentMin: nutritionalEnergyPlg.plgDayLPercentMin ?? '',
    standardPlgDayLpercentMax: nutritionalEnergyPlg.plgDayLPercentMax ?? '',
    standardPlgDayLRatio: nutritionalEnergyPlg.lProportionValue ?? '',

    // Glucide
    standardPlgDayGgramMin: nutritionalEnergyPlg.plgDayGgMin ?? '',
    standardPlgDayGgramMax: nutritionalEnergyPlg.plgDayGgMax ?? '',
    standardPlgDayGpercentMin: nutritionalEnergyPlg.plgDayGPercentMin ?? '',
    standardPlgDayGpercentMax: nutritionalEnergyPlg.plgDayGPercentMax ?? '',
    standardMealsPerDay: nutritionalEnergyPlg.numberMealDay ?? '',

    standardEnergyDistributionBsmin: nutritionalEnergyNeedPlg.energyBsMin ?? '',
    standardEnergyDistributionBsmax: nutritionalEnergyNeedPlg.energyBsMax ?? '',
    standardEnergyDistributionBtrmin:
      nutritionalEnergyNeedPlg.energyBtrMin ?? '',
    standardEnergyDistributionBtrmax:
      nutritionalEnergyNeedPlg.energyBtrMax ?? '',
    standardEnergyDistributionBcmin: nutritionalEnergyNeedPlg.energyBcMin ?? '',
    standardEnergyDistributionBcmax: nutritionalEnergyNeedPlg.energyBcMax ?? '',
    standardEnergyDistributionBtmin: nutritionalEnergyNeedPlg.energyBtMin ?? '',
    standardEnergyDistributionBtmax: nutritionalEnergyNeedPlg.energyBtMax ?? '',
    standardEnergyDistributionBP_1min:
      nutritionalEnergyNeedPlg.energyBp1Min ?? '',
    standardEnergyDistributionBP_1max:
      nutritionalEnergyNeedPlg.energyBp1Max ?? '',
    standardEnergyDistributionBP_2min:
      nutritionalEnergyNeedPlg.energyBp2Min ?? '',
    standardEnergyDistributionBP_2max:
      nutritionalEnergyNeedPlg.energyBp2Max ?? '',

    // Flatten PLG meals
    ...flatMealMap,

    // Fruits
    standardFruitDay: nutritionalIngredientDistribution.standardDayFruit ?? '',
    standardFruitBs: nutritionalIngredientDistribution.standardBsFruit ?? '',
    standardFruitBtr: nutritionalIngredientDistribution.standardBtrFruit ?? '',
    standardFruitBc: nutritionalIngredientDistribution.standardBcFruit ?? '',
    standardFruitBt: nutritionalIngredientDistribution.standardBtFruit ?? '',

    // Vegetables
    standardVegetableDay:
      nutritionalIngredientDistribution.standardDayVegetable ?? '',
    standardVegetableBs:
      nutritionalIngredientDistribution.standardBsVegetable ?? '',
    standardVegetableBtr:
      nutritionalIngredientDistribution.standardBtrVegetable ?? '',
    standardVegetableBc:
      nutritionalIngredientDistribution.standardBcVegetable ?? '',
    standardVegetableBt:
      nutritionalIngredientDistribution.standardBtVegetable ?? '',

    // Salt
    standardSaltStruct:
      nutritionalIngredientDistribution.standardStructSalt ?? '',
    standardSaltDay: nutritionalIngredientDistribution.standardDaySalt ?? '',
    standardSaltBs: nutritionalIngredientDistribution.standardBsSalt ?? '',
    standardSaltBtr: nutritionalIngredientDistribution.standardBtrSalt ?? '',
    standardSaltBc: nutritionalIngredientDistribution.standardBcSalt ?? '',
    standardSaltBt: nutritionalIngredientDistribution.standardBtSalt ?? '',
    standardSaltBp: nutritionalIngredientDistribution.standardBpSalt ?? '',

    // Micro nutrients
    totalDay: nutritionalMicro.totalDay ?? '',
    totalLunch: nutritionalMicro.totalLunch ?? '',
    totalDinner: nutritionalMicro.totalDinner ?? '',
    sugarStandardDay: nutritionalMicro.sugarStandardDay ?? '',
    waterStandardDayMin: nutritionalMicro.waterStandardDayMin ?? '',
    waterStandardDayMax: nutritionalMicro.waterStandardDayMax ?? '',
    fiberStandardDay: nutritionalMicro.fiberStandardDay ?? '',
    kaliStandardDay: nutritionalMicro.kaliStandardDay ?? '',
    cholesterolStandardDay: nutritionalMicro.cholesterolStandardDay ?? '',
    natriStandardDay: nutritionalMicro.natriStandardDay ?? '',
    cloStandardDay: nutritionalMicro.cloStandardDay ?? '',
    calciStandardDay: nutritionalMicro.calciStandardDay ?? '',
    phosphoStandardDay: nutritionalMicro.phosphoStandardDay ?? '',
    magieStandardDay: nutritionalMicro.magieStandardDay ?? '',
    ironStandardDay: nutritionalMicro.ironStandardDay ?? '',
    zincStandardDay: nutritionalMicro.zincStandardDay ?? '',
    iodStandardDay: nutritionalMicro.iodStandardDay ?? '',
    selenStandardDay: nutritionalMicro.selenStandardDay ?? '',
    copperStandardDay: nutritionalMicro.copperStandardDay ?? '',
    cromStandardDay: nutritionalMicro.cromStandardDay ?? '',
    manganStandardDay: nutritionalMicro.manganStandardDay ?? '',
    fluorStandardDay: nutritionalMicro.fluorStandardDay ?? '',
    vitaminAStandardDay: nutritionalMicro.vitaminAStandardDay ?? '',
    vitaminDStandardDay: nutritionalMicro.vitaminDStandardDay ?? '',
    vitaminEStandardDay: nutritionalMicro.vitaminEStandardDay ?? '',
    vitaminKStandardDay: nutritionalMicro.vitaminKStandardDay ?? '',
    vitaminB1StandardDay: nutritionalMicro.vitaminB1StandardDay ?? '',
    vitaminB2StandardDay: nutritionalMicro.vitaminB2StandardDay ?? '',
    vitaminB3StandardDay: nutritionalMicro.vitaminB3StandardDay ?? '',
    vitaminB5StandardDay: nutritionalMicro.vitaminB5StandardDay ?? '',
    vitaminB6StandardDay: nutritionalMicro.vitaminB6StandardDay ?? '',
    vitaminB9StandardDay: nutritionalMicro.vitaminB9StandardDay ?? '',
    vitaminB12StandardDay: nutritionalMicro.vitaminB12StandardDay ?? '',
    biotinStandardDay: nutritionalMicro.biotinStandardDay ?? '',
    cholineStandardDay: nutritionalMicro.cholineStandardDay ?? '',
    vitaminCStandardDay: nutritionalMicro.vitaminCStandardDay ?? '',
    totalUnsaturated: nutritionalMicro.totalUnsaturated ?? '',

    // Backup
    ...backupFields.reduce((acc, cur) => ({ ...acc, ...cur }), {})
  };
}

export const mapEditedDataToPayload = (editedData: any) => {
  const bmiType = editedData.bmiType;
  const payload = {
    AGE_RANGE_ID: editedData.ageRangeId || null,
    HEALTH_STATUS_IDS: Array.isArray(editedData.healthStatusIds)
      ? editedData.healthStatusIds.length > 0
        ? editedData.healthStatusIds
        : ['TTSK002']
      : editedData.healthStatusIds
        ? [editedData.healthStatusIds]
        : ['TTSK002'],
    GENDER_ID: editedData.genderId,
    WEIGHT_MIN: toNumber(editedData.weightMin),
    WEIGHT_MAX: toNumber(editedData.weightMax),
    NUTRITION_STATUS_ZSCORE:
      bmiType === AGE_TYPE_CONST.ZSCORE
        ? editedData.nutritionStatusZscore
        : null,
    NUTRITION_STATUS_BMI_CHILD:
      bmiType === AGE_TYPE_CONST.BMI_CHILD
        ? editedData.nutritionStatusBmiChild
        : null,
    NUTRITION_STATUS_BMI_ADULT:
      bmiType === AGE_TYPE_CONST.BMI_ADULT
        ? editedData.nutritionStatusBmiAdult
        : null,
    PHYSICAL_ACTIVITY_ID: editedData.physicalActivityId,
    LABOR_FACTOR: editedData.laborFactor == null ? 1 : editedData.laborFactor,
    BREAST_MILK_LEVEL: editedData.breastMilkLevel,

    ENERGY_PLG: {
      METABOLISM_BASIC: editedData.metabolismBasic,
      ENERGY_CONSUMPTION: editedData.energyConsumption,
      ENERGY_NEEDED: editedData.energyNeeded,
      ENERGY_MIN: toNumber(editedData.energyMin),
      ENERGY_MAX: toNumber(editedData.energyMax),
      COMPLEMENTARY_FOOD_ENERGY_MIN: toNumber(
        editedData.complementaryFoodEnergyMin
      ),
      COMPLEMENTARY_FOOD_ENERGY_MAX: toNumber(
        editedData.complementaryFoodEnergyMax
      ),
      BREAST_MILK_LEVEL: editedData.breastMilkLevel,
      BREAST_MILK_ENERGY: toNumber(editedData.breastMilkEnergy),
      MILK_UNITS_PER_DAY: toNumber(editedData.milkUnitsPerDay),
      PLG_DAY_P_PERCENT_MIN: toNumber(editedData.standardPlgDayPpercentMin),
      PLG_DAY_P_PERCENT_MAX: toNumber(editedData.standardPlgDayPpercentMax),
      PLG_DAY_P_G_MIN: toNumber(editedData.standardPlgDayPgramMin),
      PLG_DAY_P_G_MAX: toNumber(editedData.standardPlgDayPgramMax),
      P_PROPORTION:
        editedData.pProportionOperator && editedData.pProportionValue
          ? `${editedData.pProportionOperator}${editedData.pProportionValue}`
          : normalizeComparatorPrefix(editedData.standardPlgDayPRatio || '') || null,
      PLG_DAY_L_PERCENT_MIN: toNumber(editedData.standardPlgDayLpercentMin),
      PLG_DAY_L_PERCENT_MAX: toNumber(editedData.standardPlgDayLpercentMax),
      PLG_DAY_L_G_MIN: toNumber(editedData.standardPlgDayLgramMin),
      PLG_DAY_L_G_MAX: toNumber(editedData.standardPlgDayLgramMax),
      L_PROPORTION_OPERATOR: editedData.lProportionOperator,
      L_PROPORTION_VALUE: toNumber(editedData.lProportionValue),
      PLG_DAY_G_PERCENT_MIN: toNumber(editedData.standardPlgDayGpercentMin),
      PLG_DAY_G_PERCENT_MAX: toNumber(editedData.standardPlgDayGpercentMax),
      PLG_DAY_G_G_MIN: toNumber(editedData.standardPlgDayGgramMin),
      PLG_DAY_G_G_MAX: toNumber(editedData.standardPlgDayGgramMax),
      NUMBER_MEAL_DAY: editedData.standardMealsPerDay
    },

    NUTRITIONAL_MICRO_ELEMENTS: {
      TOTAL_DAY: toNumber(editedData.totalDay),
      TOTAL_LUNCH: toNumber(editedData.totalLunch),
      TOTAL_DINNER: toNumber(editedData.totalDinner),
      SUGAR_STANDARD_DAY: toStringOrNull(editedData.sugarStandardDay),
      WATER_STANDARD_DAY_MIN: toNumber(editedData.waterStandardDayMin),
      WATER_STANDARD_DAY_MAX: toNumber(editedData.waterStandardDayMax),
      FIBER_STANDARD_DAY: toStringOrNull(editedData.fiberStandardDay),
      KALI_STANDARD_DAY: toStringOrNull(editedData.kaliStandardDay),
      CHOLESTEROL_STANDARD_DAY: toStringOrNull(
        editedData.cholesterolStandardDay
      ),
      NATRI_STANDARD_DAY: toStringOrNull(editedData.natriStandardDay),
      CLO_STANDARD_DAY: toStringOrNull(editedData.cloStandardDay),
      CALCI_STANDARD_DAY: toStringOrNull(editedData.calciStandardDay),
      PHOSPHO_STANDARD_DAY: toStringOrNull(editedData.phosphoStandardDay),
      MAGIE_STANDARD_DAY: toStringOrNull(editedData.magieStandardDay),
      IRON_STANDARD_DAY: toStringOrNull(editedData.ironStandardDay),
      ZINC_STANDARD_DAY: toStringOrNull(editedData.zincStandardDay),
      IOD_STANDARD_DAY: toStringOrNull(
        editedData.iodStandardDay || editedData.I_OD_STANDARD_DAY
      ),
      SELEN_STANDARD_DAY: toStringOrNull(editedData.selenStandardDay),
      COPPER_STANDARD_DAY: toStringOrNull(editedData.copperStandardDay),
      CROM_STANDARD_DAY: toStringOrNull(editedData.cromStandardDay),
      MANGAN_STANDARD_DAY: toStringOrNull(editedData.manganStandardDay),
      FLUOR_STANDARD_DAY: toStringOrNull(editedData.fluorStandardDay),
      VITAMIN_A_STANDARD_DAY: toStringOrNull(editedData.vitaminAStandardDay),
      VITAMIN_D_STANDARD_DAY: toStringOrNull(editedData.vitaminDStandardDay),
      VITAMIN_E_STANDARD_DAY: toStringOrNull(editedData.vitaminEStandardDay),
      VITAMIN_K_STANDARD_DAY: toStringOrNull(editedData.vitaminKStandardDay),
      VITAMIN_B1_STANDARD_DAY: toStringOrNull(editedData.vitaminB1StandardDay),
      VITAMIN_B2_STANDARD_DAY: toStringOrNull(editedData.vitaminB2StandardDay),
      VITAMIN_B3_STANDARD_DAY: toStringOrNull(editedData.vitaminB3StandardDay),
      VITAMIN_B5_STANDARD_DAY: toStringOrNull(editedData.vitaminB5StandardDay),
      VITAMIN_B6_STANDARD_DAY: toStringOrNull(editedData.vitaminB6StandardDay),
      VITAMIN_B9_STANDARD_DAY: toStringOrNull(editedData.vitaminB9StandardDay),
      VITAMIN_B12_STANDARD_DAY: toStringOrNull(
        editedData.vitaminB12StandardDay
      ),
      BIOTIN_STANDARD_DAY: toStringOrNull(editedData.biotinStandardDay),
      CHOLINE_STANDARD_DAY: toStringOrNull(editedData.cholineStandardDay),
      VITAMIN_C_STANDARD_DAY: toStringOrNull(editedData.vitaminCStandardDay),
      BACKUP_1_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup1StandardDayMin
      ),
      BACKUP_1_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup1StandardDayMax
      ),
      BACKUP_2_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup2StandardDayMin
      ),
      BACKUP_2_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup2StandardDayMax
      ),
      BACKUP_3_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup3StandardDayMin
      ),
      BACKUP_3_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup3StandardDayMax
      ),
      BACKUP_4_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup4StandardDayMin
      ),
      BACKUP_4_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup4StandardDayMax
      ),
      BACKUP_5_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup5StandardDayMin
      ),
      BACKUP_5_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup5StandardDayMax
      ),
      BACKUP_6_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup6StandardDayMin
      ),
      BACKUP_6_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup6StandardDayMax
      ),
      BACKUP_7_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup7StandardDayMin
      ),
      BACKUP_7_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup7StandardDayMax
      ),
      BACKUP_8_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup8StandardDayMin
      ),
      BACKUP_8_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup8StandardDayMax
      ),
      BACKUP_9_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup9StandardDayMin
      ),
      BACKUP_9_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup9StandardDayMax
      ),
      BACKUP_10_STANDARD_DAY_MIN: toStringOrNull(
        editedData.backup10StandardDayMin
      ),
      BACKUP_10_STANDARD_DAY_MAX: toStringOrNull(
        editedData.backup10StandardDayMax
      ),
      TOTAl_UNSATURATED: toStringOrNull(editedData.totalUnsaturated)
    },

    NUTRITIONAL_INGREDIENT_DISTRIBUTION: {
      STANDARD_DAY_FRUIT: toNumber(editedData.standardFruitDay),
      STANDARD_BS_FRUIT: toNumber(editedData.standardFruitBs),
      STANDARD_BTR_FRUIT: toNumber(editedData.standardFruitBtr),
      STANDARD_BT_FRUIT: toNumber(editedData.standardFruitBt),
      STANDARD_BP_FRUIT: toNumber(editedData.standardFruitBp),

      STANDARD_DAY_VEGETABLE: toNumber(editedData.standardVegetableDay),
      STANDARD_BS_VEGETABLE: toNumber(editedData.standardVegetableBs),
      STANDARD_BTR_VEGETABLE: toNumber(editedData.standardVegetableBtr),
      STANDARD_BT_VEGETABLE: toNumber(editedData.standardVegetableBt),
      STANDARD_BP_VEGETABLE: toNumber(editedData.standardVegetableBp),

      STANDARD_STRUCT_SALT: toNumber(editedData.standardSaltStruct),
      STANDARD_DAY_SALT: toStringOrNull(editedData.standardSaltDay),
      STANDARD_BS_SALT: toStringOrNull(editedData.standardSaltBs),
      STANDARD_BTR_SALT: toStringOrNull(editedData.standardSaltBtr),
      STANDARD_BC_SALT: toStringOrNull(editedData.standardSaltBc),
      STANDARD_BT_SALT: toStringOrNull(editedData.standardSaltBt),
      STANDARD_BP_SALT: toStringOrNull(editedData.standardSaltBp)
    },

    NUTRITIONAL_MEAL_DISTRIBUTION: [1, 2, 3, 4, 5].map((mealTypeId) => ({
      MEAL_TYPE_ID: mealTypeId,
      ENERGY_MIN: toNumber(
        editedData[`standardEnergyDistribution${getMealKey(mealTypeId)}min`]
      ),
      ENERGY_MAX: toNumber(
        editedData[`standardEnergyDistribution${getMealKey(mealTypeId)}max`]
      ),
      PLG_P_PERCENT_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}PpercentMin`]
      ),
      PLG_P_PERCENT_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}PpercentMax`]
      ),
      PLG_P_G_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}PgramMin`]
      ),
      PLG_P_G_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}PgramMax`]
      ),
      PLG_L_PERCENT_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}LpercentMin`]
      ),
      PLG_L_PERCENT_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}LpercentMax`]
      ),
      PLG_L_G_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}LgramMin`]
      ),
      PLG_L_G_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}LgramMax`]
      ),
      PLG_G_PERCENT_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}GpercentMin`]
      ),
      PLG_G_PERCENT_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}GpercentMax`]
      ),
      PLG_G_G_MIN: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}GgramMin`]
      ),
      PLG_G_G_MAX: toNumber(
        editedData[`standardPlg${getMealLabel(mealTypeId)}GgramMax`]
      )
    })),

    NUTRITIONAL_ENERGY_NEED: {
      ENERGY_BS_MIN: toNumber(editedData.standardEnergyDistributionBsmin),
      ENERGY_BS_MAX: toNumber(editedData.standardEnergyDistributionBsmax),
      ENERGY_BTR_MIN: toNumber(editedData.standardEnergyDistributionBtrmin),
      ENERGY_BTR_MAX: toNumber(editedData.standardEnergyDistributionBtrmax),
      ENERGY_BC_MIN: toNumber(editedData.standardEnergyDistributionBcmin),
      ENERGY_BC_MAX: toNumber(editedData.standardEnergyDistributionBcmax),
      ENERGY_BT_MIN: toNumber(editedData.standardEnergyDistributionBtmin),
      ENERGY_BT_MAX: toNumber(editedData.standardEnergyDistributionBtmax),
      ENERGY_BP1_MIN: toNumber(editedData.standardEnergyDistributionBP_1min),
      ENERGY_BP1_MAX: toNumber(editedData.standardEnergyDistributionBP_1max),
      ENERGY_BP2_MIN: toNumber(editedData.standardEnergyDistributionBP_2min),
      ENERGY_BP2_MAX: toNumber(editedData.standardEnergyDistributionBP_2max)
    }
  };

  return payload;
};

const toNumber = (value: any) => {
  if (value === undefined || value === null || value === '') return null;
  const num = Number(value);
  return isNaN(num) ? null : num;
};

const toStringOrNull = (value: any) => {
  if (value === undefined || value === null || value === '') return null;
  // Convert comma to dot for API consistency
  return String(value).replace(',', '.');
};

// Helper functions:
const getMealKey = (id: number) => {
  switch (id) {
    case 1:
      return 'Bs';
    case 2:
      return 'Btr';
    case 3:
      return 'Bc';
    case 4:
      return 'Bt';
    case 5:
      return 'BP_1';
    default:
      return '';
  }
};

const getMealLabel = (id: number) => {
  switch (id) {
    case 1:
      return 'Breakfast';
    case 2:
      return 'Lunch';
    case 3:
      return 'Afternoon';
    case 4:
      return 'Dinner';
    case 5:
      return 'Extra1';
    default:
      return '';
  }
};

export const newRowStandardNutritionId = 'new_row';

export const newRowStandardNutrition = {
  id: newRowStandardNutritionId,
  ageRangeId: null,
  healthStatusIds: [],
  genderId: null,
  physicalActivityId: null,
  weightMin: null,
  weightMax: null,
  nutritionStatusZscore: null,
  nutritionStatusBmiChild: null,
  nutritionStatusBmiAdult: null,
  physicalActivityLevelName: null,
  laborFactor: 1,

  metabolismBasic: null,
  energyConsumption: null,
  energyNeeded: null,
  energyMin: null,
  energyMax: null,
  complementaryFoodEnergyMin: null,
  complementaryFoodEnergyMax: null,
  breastMilkLevel: null,
  breastMilkEnergy: null,
  milkUnitsPerDay: null,
  standardPlgDayPpercentMin: null,
  standardPlgDayPpercentMax: null,
  standardPlgDayPgramMin: null,
  standardPlgDayPgramMax: null,
  standardPlgDayPRatio: null,
  pProportionOperator: '=',
  pProportionValue: null,
  standardPlgDayLpercentMin: null,
  standardPlgDayLpercentMax: null,
  standardPlgDayLgramMin: null,
  standardPlgDayLgramMax: null,
  lProportionOperator: null,
  lProportionValue: null,
  standardPlgDayGpercentMin: null,
  standardPlgDayGpercentMax: null,
  standardPlgDayGgramMin: null,
  standardPlgDayGgramMax: null,
  standardMealsPerDay: null,

  totalDay: null,
  totalLunch: null,
  totalDinner: null,
  sugarStandardDay: null,
  waterStandardDayMin: null,
  waterStandardDayMax: null,
  fiberStandardDayMin: null,
  fiberStandardDayMax: null,
  kaliStandardDay: null,
  cholesterolStandardDay: null,
  natriStandardDay: null,
  cloStandardDay: null,
  calciStandardDay: null,
  phosphoStandardDay: null,
  magieStandardDay: null,
  ironStandardDay: null,
  zincStandardDay: null,
  iodStandardDay: null,
  selenStandardDay: null,
  copperStandardDay: null,
  cromStandardDay: null,
  manganStandardDay: null,
  fluorStandardDay: null,
  vitaminAStandardDay: null,
  vitaminDStandardDay: null,
  vitaminEStandardDay: null,
  vitaminKStandardDay: null,
  vitaminB1StandardDay: null,
  vitaminB2StandardDay: null,
  vitaminB3StandardDay: null,
  vitaminB5StandardDay: null,
  vitaminB6StandardDay: null,
  vitaminB9StandardDay: null,
  vitaminB12StandardDay: null,
  biotinStandardDay: null,
  cholineStandardDay: null,
  vitaminCStandardDay: null,

  backup1StandardDayMin: null,
  backup1StandardDayMax: null,
  backup2StandardDayMin: null,
  backup2StandardDayMax: null,
  backup3StandardDayMin: null,
  backup3StandardDayMax: null,
  backup4StandardDayMin: null,
  backup4StandardDayMax: null,
  backup5StandardDayMin: null,
  backup5StandardDayMax: null,
  backup6StandardDayMin: null,
  backup6StandardDayMax: null,
  backup7StandardDayMin: null,
  backup7StandardDayMax: null,
  backup8StandardDayMin: null,
  backup8StandardDayMax: null,
  backup9StandardDayMin: null,
  backup9StandardDayMax: null,
  backup10StandardDayMin: null,
  backup10StandardDayMax: null,
  totalUnsaturated: null,

  standardFruitDay: null,
  standardFruitBs: null,
  standardFruitBtr: null,
  standardFruitBt: null,
  standardFruitBp: null,

  standardVegetableDay: null,
  standardVegetableBs: null,
  standardVegetableBtr: null,
  standardVegetableBt: null,
  standardVegetableBp: null,

  standardSaltStruct: null,
  standardSaltDay: null,
  standardSaltBs: null,
  standardSaltBtr: null,
  standardSaltBc: null,
  standardSaltBt: null,
  standardSaltBp: null,

  // Energy Need (BP, BT, BC...)
  standardEnergyDistributionBsmin: null,
  standardEnergyDistributionBsmax: null,
  standardEnergyDistributionBtrmin: null,
  standardEnergyDistributionBtrmax: null,
  standardEnergyDistributionBcmin: null,
  standardEnergyDistributionBcmax: null,
  standardEnergyDistributionBtmin: null,
  standardEnergyDistributionBtmax: null,
  standardEnergyDistributionBP_1min: null,
  standardEnergyDistributionBP_1max: null,
  standardEnergyDistributionBP_2min: null,
  standardEnergyDistributionBP_2max: null
};
